"use client";

import { BellRing, Phone, MessageCircle, Mail, CheckCircle2, Clock, Zap, PhoneCall, PhoneMissed, Voicemail, PhoneIncoming } from "lucide-react";
import { rmById, type Reminder, type CallLogEntry } from "@/lib/demos/stockBrokingData";

interface RemindersPanelProps {
  reminders: Reminder[];
  callLog: CallLogEntry[];
  onToggleReminder: (id: string) => void;
}

const CHANNEL_ICON = { call: Phone, whatsapp: MessageCircle, email: Mail };
const STATUS_STYLE: Record<Reminder["status"], string> = {
  overdue: "border-rose-500/40 bg-rose-500/5",
  due: "border-amber-500/40 bg-amber-500/5",
  upcoming: "border-slate-200 bg-slate-50",
  done: "border-emerald-500/30 bg-emerald-500/5 opacity-70",
};
const STATUS_TAG: Record<Reminder["status"], string> = {
  overdue: "bg-rose-500/15 text-rose-700",
  due: "bg-amber-500/15 text-amber-700",
  upcoming: "bg-slate-200 text-slate-600",
  done: "bg-emerald-500/15 text-emerald-700",
};
const OUTCOME_ICON = { connected: PhoneCall, missed: PhoneMissed, callback: PhoneIncoming, voicemail: Voicemail };
const OUTCOME_STYLE = {
  connected: "text-emerald-600",
  missed: "text-rose-600",
  callback: "text-amber-600",
  voicemail: "text-blue-600",
};

export function RemindersPanel({ reminders, callLog, onToggleReminder }: RemindersPanelProps) {
  const sorted = [...reminders].sort((a, b) => {
    const order = { overdue: 0, due: 1, upcoming: 2, done: 3 };
    return order[a.status] - order[b.status];
  });
  const pending = reminders.filter((r) => r.status !== "done").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* Reminders */}
      <div className="lg:col-span-3 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-emerald-600" /> Automated Follow-ups &amp; Reminders
          </h2>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/20">
            {pending} pending
          </span>
        </div>

        <div className="space-y-2.5">
          {sorted.map((r) => {
            const Icon = CHANNEL_ICON[r.channel];
            const rm = rmById(r.rmId);
            return (
              <div key={r.id} className={`p-3.5 rounded-xl border transition-all ${STATUS_STYLE[r.status]}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-emerald-600">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold ${r.status === "done" ? "text-slate-500 line-through" : "text-slate-900"}`}>{r.title}</span>
                      {r.auto && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 flex items-center gap-0.5">
                          <Zap className="w-2.5 h-2.5" /> Auto
                        </span>
                      )}
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_TAG[r.status]}`}>{r.status}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {r.clientName} ({r.clientCode}) · {rm?.name ?? "—"}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {r.due}</p>
                  </div>
                  <button
                    onClick={() => onToggleReminder(r.id)}
                    className={`p-1.5 rounded-lg shrink-0 transition-colors ${r.status === "done" ? "text-emerald-600 bg-emerald-500/10" : "text-slate-500 hover:text-emerald-600 hover:bg-emerald-500/10"}`}
                    title={r.status === "done" ? "Mark as pending" : "Mark as done"}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Call log */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-600" /> Click-to-Call Log
          </h2>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
            Integration-ready
          </span>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-200">
          {callLog.map((c) => {
            const Icon = OUTCOME_ICON[c.outcome];
            return (
              <div key={c.id} className="p-3.5 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 ${OUTCOME_STYLE[c.outcome]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900 truncate">{c.clientName}</span>
                    <span className={`text-[9px] font-bold uppercase ${OUTCOME_STYLE[c.outcome]}`}>{c.outcome}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">{c.rmName} · {c.direction} · {c.time} · {c.duration}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.recording && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">REC</span>}
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="p-1.5 rounded-lg bg-emerald-600/15 hover:bg-emerald-600 text-emerald-600 hover:text-white transition-colors" title="Call back">
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Click-to-call opens the device dialer via <code className="text-slate-500">tel:</code>. Production connects a cloud-telephony provider (Exotel / Knowlarity / Twilio) for in-app dialing, recording &amp; IVR.
        </p>
      </div>
    </div>
  );
}
